import React, { useEffect } from "react";
import { useHistory } from "@core/routing/useHistory";
import { Path, searchParams } from "@core/routing/path";
import { Carousel as PdfCarousel } from "@domain/carousel/carousel";
import { Group } from "@domain/group/group";
import { Article } from "@domain/lms/article/article";
import { Category } from "@domain/lms/category/category";
import { ContentLanguage } from "@domain/lms/contentLanguage";
import { ContentLevel } from "@domain/lms/contentLevel";
import { Course, CourseDuration, CourseProvider } from "@domain/lms/course/course";
import { Quiz } from "@domain/lms/quiz/quiz";
import { Organization } from "@domain/organization/organization";
import { OrganizationSubType, OrganizationType } from "@domain/organization/organizationType";
import { useSearchResult, useSuggestions } from "@domain/search/hook";
import { SearchDomain } from "@domain/search/searchDomain";
import { Person } from "@domain/user/user";
import { useIsMobile } from "@utils/hooks";
import { Carousel } from "@ui/components/carousel/carousel";

const CAROUSEL_SIZE = 10;

interface SearchCarouselProps {
	renderItem: (item: Person | Course | Article | Quiz | Organization | Group | PdfCarousel) => React.ReactNode;
	renderTitle: (count?: number) => string;
	itemHeight?: number;
	itemWidth?: number;
	query: string;
	domain: SearchDomain;
	categories?: Category[];
	levels?: ContentLevel[];
	languages?: ContentLanguage[];
	durations?: CourseDuration[];
	providers?: CourseProvider[];
	orgTypes?: OrganizationType[];
	orgSubTypes?: OrganizationSubType[];
	onTotalChanged: (total: number) => void;
}
export const SearchCarousel: React.FC<SearchCarouselProps> = ({
	renderItem,
	renderTitle,
	itemHeight,
	itemWidth,
	query,
	domain,
	categories,
	levels,
	languages,
	durations,
	providers,
	orgTypes,
	orgSubTypes,
	onTotalChanged,
	...htmlProps
}) => {
	const {
		result: courses,
		loading,
		totalElements,
	} = useSearchResult(query, domain, categories, levels, languages, durations, providers, orgTypes, orgSubTypes);
	const history = useHistory();
	useEffect(() => {
		if (totalElements !== undefined) {
			onTotalChanged(totalElements);
		}
	}, [totalElements]);

	return (
		<Carousel
			{...htmlProps}
			onSeeAllAction={
				!!totalElements && totalElements > CAROUSEL_SIZE
					? () =>
							history.push({
								pathname: Path.Search,
								search: searchParams(query, domain, categories, levels, languages, durations, providers),
							})
					: undefined
			}
			style={{ marginTop: 20 }}
			items={courses.slice(0, CAROUSEL_SIZE) ?? []}
			renderItem={renderItem}
			title={renderTitle(totalElements)}
			loading={loading}
			itemHeight={itemHeight}
			itemWidth={itemWidth}
		/>
	);
};

interface SuggestCarouselProps extends React.HTMLAttributes<HTMLDivElement> {
	title: string;
	renderItem: (item: Person | Course | Article | Quiz | Organization | Group | PdfCarousel) => React.ReactNode;
	domain: SearchDomain;
	itemHeight?: number;
}
export const SuggestCarousel: React.FC<SuggestCarouselProps> = ({
	domain,
	renderItem,
	title,
	itemHeight,
	...htmlProps
}) => {
	const newSize = CAROUSEL_SIZE;
	const { result: suggestions, loading } = useSuggestions(newSize, domain);
	const history = useHistory();
	const isMobile = useIsMobile();
	// ToDo: need carousel suggestions API, for now we use the search

	return (
		<Carousel
			{...htmlProps}
			onSeeAllAction={() =>
				history.push({
					pathname: Path.Search,
					search: searchParams("", domain),
				})
			}
			style={{ marginTop: 20 }}
			items={suggestions ?? []}
			renderItem={renderItem}
			title={title}
			loading={loading}
			itemHeight={domain === "ARTICLE" && isMobile ? 484 : itemHeight}
			itemWidth={domain === "ARTICLE" ? 310 : undefined}
		/>
	);
};
